<template>
    <ModalContent
        flat-chrome
        :help-content-id="`popup-provider-${current_entity_profile}`"
    >
        <template slot="title">{{ title }}</template>
        <template slot="content"
            ><div class="stitch-modal general-settings-reference stitch-form">
                <Warning :elevation="2" v-if="isExternal" class="mb-2">
                    {{ $translate("instructionsExternal") }}
                </Warning>

                <v-card
                    class="stitch-modal__card provider-configuration-card"
                    elevation="0"
                    ><v-card-text
                        ><h5 class="stitch-modal__title">
                            <v-icon size="16">mdi-domain</v-icon
                            ><span>{{
                                $translate("providerConfiguration")
                            }}</span>
                        </h5>

                        <v-row>
                            <v-col cols="12" sm="6">
                                <CompanySearchAutocomplete
                                    v-model="provider.name"
                                    :error-messages="
                                        provider_saving_errors.name
                                    "
                                    :label="$translate('labels.name')"
                                    :placeholder="
                                        $translate('placeholders.companyName')
                                    "
                                    @company-selected="onCompanySelected"
                                    :dense="true"
                                    outlined
                                    @keydown="handleKeyPress"
                                    :autofocus="true"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <CompanySearchAutocomplete
                                    :value="provider.legal_name"
                                    :error-messages="
                                        provider_saving_errors.legal_name
                                    "
                                    :label="$translate('labels.legal_name')"
                                    :placeholder="
                                        $translate('placeholders.legal_name')
                                    "
                                    @input="onLegalNameInput"
                                    @company-selected="
                                        onLegalNameCompanySelected
                                    "
                                    :dense="true"
                                    outlined
                                    @keydown="handleKeyPress"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="provider.code"
                                    :error-messages="
                                        provider_saving_errors.code
                                    "
                                    :label="$translate('labels.code')"
                                    :placeholder="$translate('codePlaceholder')"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    name="code"
                                    @keydown="handleKeyPress"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" v-if="!isExternal">
                                <v-text-field
                                    v-model="provider.upc_prefix"
                                    :error-messages="
                                        provider_saving_errors.upc_prefix
                                    "
                                    :label="$translate('labels.upc_prefix')"
                                    :placeholder="
                                        $translate('placeholders.upc_prefix')
                                    "
                                    dense
                                    outlined
                                    hide-details="auto"
                                    @keydown="handleKeyPress"
                                >
                                    <template #label
                                        ><span
                                            >{{ $translate("labels.upc_prefix")
                                            }}<StitchHint
                                                :hint="
                                                    $translate(
                                                        'hints.upc_prefix'
                                                    )
                                                " /></span
                                    ></template>
                                </v-text-field>
                            </v-col>

                            <v-col cols="12" sm="6">
                                <CompanySiren
                                    v-model="provider.siren"
                                    :error-messages="
                                        provider_saving_errors.siren
                                    "
                                    :label="$translate('labels.siren')"
                                    :placeholder="
                                        $translate('placeholders.siren')
                                    "
                                    @company-selected="onCompanySelected"
                                    :dense="true"
                                    outlined
                                    @keydown="handleKeyPress"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="provider.vat_number"
                                    :error-messages="
                                        provider_saving_errors.vat_number
                                    "
                                    :label="$translate('labels.vat_number')"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    @keydown="handleKeyPress"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" v-if="!isExternal">
                                <v-text-field
                                    :value="
                                        provider.vat_rate
                                            ? decimal(provider.vat_rate).times(
                                                  100
                                              )
                                            : null
                                    "
                                    @input="
                                        provider.vat_rate = $event
                                            ? decimal($event).dividedBy(100)
                                            : null
                                    "
                                    :error-messages="
                                        provider_saving_errors.vat_rate
                                    "
                                    :label="$translate('labels.vat_rate')"
                                    :disabled="isExternal"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    @keydown="handleKeyPress"
                                />
                            </v-col>

                            <v-col cols="12" md="6" v-if="!provider.entity">
                                <v-select
                                    v-model="provider.prices_include_vat"
                                    :disabled="isExternal"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    :items="[
                                        {
                                            text: $translate(
                                                'labels.prices_exclude_vat'
                                            ),
                                            value: false,
                                        },
                                        {
                                            text: $translate(
                                                'labels.prices_include_vat'
                                            ),
                                            value: true,
                                        },
                                    ]"
                                    :label="$translate('labels.vat')"
                                    @keydown="handleKeyPress"
                                />
                            </v-col>

                            <v-col cols="12" class="pt-0">
                                <AddressForm
                                    :title="$translate('labels.address')"
                                    :value="provider.address || {}"
                                    @input="provider.address = $event"
                                    :errors="provider_saving_errors.address"
                                    :dense="true"
                                    outlined
                                    @keydown="handleKeyPress"
                                />
                            </v-col>
                        </v-row>
                        <v-row class="mb-4">
                            <v-col cols="12">
                                <TagInput
                                    v-model="provider.tags"
                                    object-type="provider"
                                    :label="$translate('labels.tags')"
                                    :placeholder="
                                        $translate('placeholders.tags')
                                    "
                                    :dense="true"
                                    outlined
                                    hide-details="auto"
                                    @keydown="handleKeyPress"
                                />
                            </v-col>
                            <v-col cols="12">
                                <v-textarea
                                    v-model="provider.comments"
                                    :error-messages="
                                        provider_saving_errors.comments
                                    "
                                    :label="$translate('labels.comments')"
                                    :placeholder="
                                        $translate('placeholders.comments')
                                    "
                                    dense
                                    outlined
                                    hide-details="auto"
                                    @keydown="handleKeyPress"
                                />
                            </v-col>
                        </v-row> </v-card-text></v-card
                ><v-card class="stitch-modal__card" elevation="0"
                    ><v-card-text
                        ><h5 class="stitch-modal__title">
                            <v-icon size="16">mdi-credit-card-outline</v-icon
                            ><span>{{
                                $translate("paymentMethodSection")
                            }}</span>
                        </h5>
                        <v-row dense>
                            <v-col cols="12" sm="4">
                                <v-select
                                    v-model="provider.preferred_payment_method"
                                    :items="paymentMethodChoices"
                                    :label="
                                        $translate(
                                            'labels.preferredPaymentMethod'
                                        )
                                    "
                                    :error-messages="
                                        provider_saving_errors.preferred_payment_method
                                    "
                                    dense
                                    outlined
                                    hide-details="auto"
                                    clearable
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="provider.payee_iban"
                                    :error-messages="
                                        provider_saving_errors.payee_iban
                                    "
                                    :label="$translate('labels.payeeIban')"
                                    dense
                                    outlined
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="provider.payee_bic"
                                    :error-messages="
                                        provider_saving_errors.payee_bic
                                    "
                                    :label="$translate('labels.payeeBic')"
                                    dense
                                    outlined
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="provider.payee_account_holder"
                                    :error-messages="
                                        provider_saving_errors.payee_account_holder
                                    "
                                    :label="
                                        $translate('labels.payeeAccountHolder')
                                    "
                                    dense
                                    outlined
                                    hide-details="auto"
                                />
                            </v-col>
                        </v-row> </v-card-text
                ></v-card></div
        ></template>
        <template slot="bottom">
            <Button
                :text="$translate('buttons.cancel')"
                @click="cancelEditClicked"
                class="my-2 my-sm-4 w-100 w-sm-auto"
                ghost
            />

            <Button
                class="my-2 my-sm-4 w-100 w-sm-auto ml-sm-3"
                :text="saveText"
                :disabled="canSave || provider_saving"
                :loading="provider_saving"
                @click="saveInformationClicked"
            />
        </template>
    </ModalContent>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import GlobalMixins from "@/mixins/global";
import CompanySearchAutocomplete from "@/components/common/company-search-autocomplete.vue";
import CompanySiren from "@/components/common/company-siren.vue";
import TagInput from "@/components/common/tag-input.vue";

export default {
    mixins: [GlobalMixins],
    components: {
        CompanySearchAutocomplete,
        CompanySiren,
        TagInput,
    },
    props: {
        params: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapState("editprovider", [
            "provider",
            "provider_saving",
            "provider_saving_errors",
        ]),
        canSave() {
            if (this.isExternal) {
                return false;
            }
            return !this.provider.name;
        },
        isExternal() {
            return !!this.provider.entity || !!this.provider.parent;
        },

        isNew() {
            return !this.provider.id;
        },
        saveText() {
            if (this.isNew) {
                return this.$translate("buttons.createProvider");
            }
            return this.$translate("buttons.save");
        },
        title() {
            return this.provider?.id
                ? this.$translate("title.editProvider")
                : this.$translate("title.createProvider");
        },
        paymentMethodChoices() {
            return [
                { text: this.$translate("paymentMethods.transfer"), value: "transfer" },
                { text: this.$translate("paymentMethods.direct_debit"), value: "direct_debit" },
                {
                    text: this.$translate("paymentMethods.bill_of_exchange"),
                    value: "bill_of_exchange",
                },
                { text: this.$translate("paymentMethods.check"), value: "check" },
                { text: this.$translate("paymentMethods.card"), value: "card" },
                { text: this.$translate("paymentMethods.cash"), value: "cash" },
            ];
        },
    },
    methods: {
        ...mapActions("editprovider", [
            "fetchProvider",
            "deleteProvider",
            "saveProvider",
            "init",
        ]),
        ...mapMutations("editprovider", ["updateProviderSavingErrors"]),
        async saveInformationClicked() {
            window.setTimeout(async () => {
                let errors = {};
                if (!this.provider.name) {
                    errors.name = [this.$translate("validation.required")];
                }

                if (Object.keys(errors).length) {
                    this.updateProviderSavingErrors(errors);
                    return;
                }

                this.saveProviderDirect();
            }, 0);
        },
        async saveProviderDirect() {
            try {
                const result = await this.saveProvider({
                    instance: this.provider,
                });
                if (result.status == 201) {
                    this.addAlert(this.$translate("messages.providerCreated"));
                } else {
                    this.addAlert(this.$translate("messages.providerUpdated"));
                }
                this.hideModal({ result: result.data });
            } catch (error) {
                this.$swal({
                    title: this.$translate("messages.error"),
                    html: this.$utils.resultToSwalContent(this, error),
                    type: "error",
                });
            }
        },
        cancelEditClicked() {
            this.hideModal();
        },

        // Handle Enter key for automatic save
        handleKeyPress(event) {
            // Save if Enter key is pressed
            if (event.key === "Enter") {
                event.preventDefault();

                // Check if we can save
                if (!this.canSave && !this.provider_saving) {
                    this.saveInformationClicked();
                }
            }
        },

        // Handle company selection from SIREN search or autocomplete
        onCompanySelected(companyData) {
            const newAddress =
                companyData.line1 ||
                companyData.line2 ||
                companyData.city ||
                companyData.postal_code ||
                companyData.country
                    ? {
                          ...this.provider.address,
                          line1: companyData.line1 || "",
                          line2: companyData.line2 || "",
                          city: companyData.city || "",
                          postal_code: companyData.postal_code || "",
                          country: companyData.country || "France",
                      }
                    : this.provider.address;

            this.provider.name = companyData.name || "";
            this.provider.legal_name = companyData.legal_name || "";
            this.provider.siren = companyData.siren || "";
            this.provider.vat_number = companyData.vat_number || "";
            this.provider.share_capital = companyData.share_capital || "";
            this.provider.legal_structure = companyData.legal_structure || "";
            this.provider.rcs = companyData.rcs || "";
            this.provider.naf_code = companyData.naf_code || "";
            this.provider.address = newAddress;
            this.$forceUpdate();
        },

        // Handle manual input for legal_name field
        onLegalNameInput(value) {
            this.provider.legal_name = value;
        },

        // Handle company selection for legal_name field (only fills legal_name)
        onLegalNameCompanySelected(companyData) {
            const newAddress =
                companyData.line1 ||
                companyData.line2 ||
                companyData.city ||
                companyData.postal_code ||
                companyData.country
                    ? {
                          ...this.provider.address,
                          line1: companyData.line1 || "",
                          line2: companyData.line2 || "",
                          city: companyData.city || "",
                          postal_code: companyData.postal_code || "",
                          country: companyData.country || "France",
                      }
                    : this.provider.address;

            this.provider.legal_name = companyData.name || "";
            this.provider.siren = companyData.siren || "";
            this.provider.vat_number = companyData.vat_number || "";
            this.provider.share_capital = companyData.share_capital || "";
            this.provider.legal_structure = companyData.legal_structure || "";
            this.provider.rcs = companyData.rcs || "";
            this.provider.naf_code = companyData.naf_code || "";
            this.provider.address = newAddress;
            this.$forceUpdate();
        },
    },
    created() {
        this.init(this.params);
    },
    i18n: {
        messages: {
            en: {
                instructionsExternal:
                    "This provider is connected with you on Safetee. You can modify the information about it, but it will be overwritten if the provider modifies them in their space.",
                codePlaceholder: "Provider code in your system",
                buttons: {
                    createProvider: "Create provider",
                    save: "Save",
                    cancel: "Cancel",
                },
                title: {
                    editProvider: "Edit provider",
                    createProvider: "Create provider",
                },
                labels: {
                    code: "Provider code",
                    name: "Name (*)",
                    legal_name: "Legal name",
                    vat: "VAT",
                    vat_rate: "VAT rate (%)",
                    prices_exclude_vat: "Prices excluding VAT",
                    prices_include_vat: "Prices including VAT",
                    siren: "SIREN",
                    vat_number: "VAT number",
                    address: "Address",
                    comments: "Comments",
                    tags: "Tags",
                    upc_prefix: "UPC prefix",
                    preferredPaymentMethod: "Preferred payment method",
                    payeeIban: "Payee IBAN",
                    payeeBic: "Payee BIC",
                    payeeAccountHolder: "Account holder",
                },
                paymentMethods: {
                    transfer: "Wire transfer",
                    direct_debit: "Direct debit",
                    bill_of_exchange: "Accepted bill of exchange",
                    check: "Check",
                    card: "Card terminal",
                    cash: "Cash",
                },
                placeholders: {
                    companyName: "Provider name...",
                    siren: "Enter a SIREN (9 digits)...",
                    comments: "Enter your comments here...",
                    tags: "Add tags to categorize this provider...",
                    legal_name: "Legal name (if different from name)...",
                    upc_prefix: "e.g. PROV, SUPPLIER...",
                },
                hints: {
                    legal_name: "Legal name (if different from name)",
                    upc_prefix: "Prefix for automatically generated UPC codes for this provider's products. If not set, the provider code or name will be used.",
                },
                providerConfiguration: "Provider configuration",
                paymentMethodSection: "Payment",
                messages: {
                    error: "Error",
                    providerCreated: "Provider created",
                    providerUpdated: "Provider updated",
                },
                validation: {
                    required: "This field is required",
                },
            },
            fr: {
                instructionsExternal:
                    "Ce fournisseur est connecté avec vous sur Safetee. Vous pouvez modifier les informations le concernant mais elles seront écrasées si le fournisseur les modifie dans son espace.",
                codePlaceholder: "Code du fournisseur dans votre système",
                buttons: {
                    createProvider: "Créer le fournisseur",
                    save: "Enregistrer",
                    cancel: "Annuler",
                },
                title: {
                    editProvider: "Éditer le fournisseur",
                    createProvider: "Créer le fournisseur",
                },
                labels: {
                    code: "Code fournisseur",
                    name: "Nom (*)",
                    legal_name: "Raison sociale",
                    vat: "TVA",
                    vat_rate: "Taux de TVA (%)",
                    prices_exclude_vat: "Prix HT",
                    prices_include_vat: "Prix TTC",
                    siren: "SIREN",
                    vat_number: "Numéro de TVA",
                    address: "Adresse",
                    comments: "Commentaires",
                    tags: "Tags",
                    upc_prefix: "Préfixe UPC",
                    preferredPaymentMethod: "Mode de paiement préféré",
                    payeeIban: "IBAN du bénéficiaire",
                    payeeBic: "BIC du bénéficiaire",
                    payeeAccountHolder: "Titulaire du compte",
                },
                paymentMethods: {
                    transfer: "Virement",
                    direct_debit: "Prélèvement",
                    bill_of_exchange: "Effet accepté",
                    check: "Chèque",
                    card: "TPE",
                    cash: "Espèces",
                },
                placeholders: {
                    companyName: "Nom du fournisseur...",
                    siren: "Entrez un SIREN (9 chiffres)...",
                    comments: "Entrez vos notes personnelles ici...",
                    tags: "Ajoutez des tags pour catégoriser ce fournisseur...",
                    legal_name: "Raison sociale (si différent du nom)...",
                    upc_prefix: "ex: PROV, FOURNISSEUR...",
                },
                hints: {
                    legal_name: "Raison sociale (si différent du nom)",
                    upc_prefix: "Préfixe pour les codes UPC générés automatiquement pour les produits de ce fournisseur. Si non défini, le code ou le nom du fournisseur sera utilisé.",
                },

                providerConfiguration: "Configuration du fournisseur",
                paymentMethodSection: "Paiement",
                messages: {
                    error: "Erreur",
                    providerCreated: "Fournisseur créé",
                    providerUpdated: "Fournisseur mis à jour",
                },
                validation: {
                    required: "Ce champ est requis",
                },
            },
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/_stitch-form.scss";
.provider-configuration-card { overflow: visible; position: relative; z-index: 1; }
</style>
